/**
 * Client time -- the Time tab on the client page (T&B-1 PR 5; spec,
 * "Screens": "Entries, WIP, recovery and write-off for that client";
 * everyone, values managers).
 *
 * Decision 5 settles who sees what, and the server enforces it: a manager
 * sees everyone's time on the client (not other people's drafts), with
 * stamped values, the client's WIP as it stands today and recovery per
 * fixed-price period; staff see their own entries and disbursements only,
 * with no values. Read-only; CSV and print, like the reports.
 */

import { useCallback, useState } from 'react'
import { api, type AuthState, type ClientTime, type WipLine } from '../api'
import { downloadCsv } from '../csv'
import { csvRand, sectionCls, tdCls, thCls, Toolbar, useReport } from '../pages/ReportsPage'
import { ErrorState, LoadingState } from './States'
import { inputCls, labelCls, rand, StatusChip } from './TimeCapture'

const numCls = `${tdCls} text-right tabular-nums whitespace-nowrap`
const numTh = `${thCls} text-right`
const pct = (bp: number | null) => (bp === null ? '—' : `${(bp / 100).toFixed(1)} %`)
const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
const KIND_WORDS: Record<WipLine['kind'], string> = {
  fixed: 'Fixed price',
  hourly: 'Hourly',
  unpriced: 'No agreed price',
  disbursement: 'Disbursement',
}

export function ClientTimeTab({ clientId, auth, version = 0 }: { clientId: string; auth: AuthState; version?: number }) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const signedIn = auth.mode === 'cognito' && auth.user !== null
  const load = useCallback(
    () => (signedIn ? api.clientTime(clientId, from || undefined, to || undefined) : Promise.reject(new Error('Time is per person, so this tab needs sign-in. This server is running without Cognito (local development).'))),
    // version: Log time on the page header bumps it, so the new draft appears.
    [clientId, from, to, signedIn, version]
  )
  const { data, error } = useReport<ClientTime>(load)

  const csv = () => {
    if (data === null) return
    const money = data.scope === 'all'
    downloadCsv(
      `client-${data.client.clientNumber}-time-${data.from}-to-${data.to}.csv`,
      ['Date', 'Person', 'Billing job', 'Task', 'Narrative', 'Hours', 'Status', ...(money ? ['Value'] : [])],
      data.entries.map((e) => [
        e.workDate,
        e.person.name,
        e.job.name,
        e.task ?? '',
        e.narrative ?? '',
        e.hours,
        e.status,
        ...(money ? [e.valueCents === null ? '' : csvRand(e.valueCents)] : []),
      ])
    )
  }

  return (
    <div>
      <Toolbar onCsv={csv}>
        <label className={labelCls}>
          From
          <input type="date" className={inputCls} value={from || data?.from || ''} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className={labelCls}>
          To
          <input type="date" className={inputCls} value={to || data?.to || ''} onChange={(e) => setTo(e.target.value)} />
        </label>
      </Toolbar>
      {error ? (
        <ErrorState message={error} />
      ) : data === null ? (
        <LoadingState what="this client's time" />
      ) : (
        <>
          <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
            {data.scope === 'all'
              ? 'Everyone’s time on this client except other people’s drafts, with the values stamped at approval. Amounts exclude VAT.'
              : 'Your own time and disbursements on this client. Other people’s time and the value of time are for managers (decision 5).'}
          </p>
          {data.wip !== null && <WipBlock data={data} />}
          {data.recovery !== null && <RecoveryBlock data={data} />}
          <Entries data={data} />
          {data.disbursements.length > 0 && <Disbursements data={data} />}
        </>
      )}
    </div>
  )
}

function WipBlock({ data }: { data: ClientTime }) {
  const wip = data.wip!
  return (
    <section className={sectionCls}>
      <h2 className="mb-2 mt-0 font-heading text-[15px] font-bold text-eq-navy">
        WIP today <span className="font-normal text-eq-neutral">· {rand(wip.totals.wipCents)}</span>
      </h2>
      {wip.lines.length === 0 ? (
        <p className="m-0 text-[13.5px] text-eq-neutral">No unbilled approved work for this client.</p>
      ) : (
        <table className="w-full border-collapse text-[13.5px]">
          <thead className="text-[12px] text-eq-neutral">
            <tr>
              <th className={thCls}>Kind</th>
              <th className={thCls}>Billing job</th>
              <th className={thCls}>Period or date</th>
              <th className={thCls}>Who</th>
              <th className={numTh}>Hours</th>
              <th className={numTh}>Time value</th>
              <th className={numTh}>WIP</th>
              <th className={thCls}>Age</th>
            </tr>
          </thead>
          <tbody>
            {wip.lines.map((l, i) => (
              <tr key={i} className="border-t border-eq-border">
                <td className={`${tdCls} ${l.kind === 'unpriced' ? 'text-eq-warning' : ''}`}>{KIND_WORDS[l.kind]}</td>
                <td className={tdCls}>
                  {l.job?.name ?? l.detail}
                  {l.job !== null && l.detail && <div className="text-[12px] text-eq-helper">{l.detail}</div>}
                </td>
                <td className={`${tdCls} whitespace-nowrap`}>{l.label}</td>
                <td className={tdCls}>{l.people.join(', ')}</td>
                <td className={numCls}>{l.hours ?? ''}</td>
                <td className={numCls}>{l.kind === 'disbursement' ? '' : rand(l.valueCents)}</td>
                <td className={`${numCls} font-semibold`}>{rand(l.wipCents)}</td>
                <td className={tdCls}>{l.band}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function RecoveryBlock({ data }: { data: ClientTime }) {
  const r = data.recovery!
  if (r.periods.length === 0 && r.hourly.valueCents === 0) return null
  return (
    <section className={sectionCls}>
      <h2 className="mb-2 mt-0 font-heading text-[15px] font-bold text-eq-navy">Recovery and write-off</h2>
      {r.periods.length > 0 && (
        <table className="w-full border-collapse text-[13.5px]">
          <thead className="text-[12px] text-eq-neutral">
            <tr>
              <th className={thCls}>Billing job</th>
              <th className={thCls}>Period</th>
              <th className={numTh}>Price</th>
              <th className={numTh}>Hours</th>
              <th className={numTh}>Time value</th>
              <th className={numTh}>Write-off</th>
              <th className={numTh}>Gain</th>
              <th className={numTh}>Recovery</th>
            </tr>
          </thead>
          <tbody>
            {r.periods.map((p, i) => (
              <tr key={i} className="border-t border-eq-border">
                <td className={tdCls}>{p.job.name}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  {p.period.label}
                  {!p.ended && <div className="text-[12px] text-eq-helper">still running</div>}
                </td>
                <td className={numCls}>{rand(p.priceCents)}</td>
                <td className={numCls}>{p.hours}</td>
                <td className={numCls}>{rand(p.valueCents)}</td>
                <td className={numCls}>{p.writeOffCents === 0 ? '' : rand(p.writeOffCents)}</td>
                <td className={numCls}>{p.gainCents === 0 ? '' : rand(p.gainCents)}</td>
                <td className={numCls}>{pct(p.recoveryBp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {r.hourly.valueCents > 0 && (
        <p className="mb-0 mt-2 text-[12.5px] text-eq-helper">
          Plus {r.hourly.hours} h of hourly time worth {rand(r.hourly.valueCents)} in the range — its recovery is known once it is billed (T&amp;B-3).
        </p>
      )}
    </section>
  )
}

function Entries({ data }: { data: ClientTime }) {
  const money = data.scope === 'all'
  return (
    <section className={sectionCls}>
      <h2 className="mb-2 mt-0 font-heading text-[15px] font-bold text-eq-navy">
        Time {dayLabel(data.from)} – {dayLabel(data.to)}{' '}
        <span className="font-normal text-eq-neutral">
          · {data.totals.hours} h{data.totals.approvedHours !== data.totals.hours && <> ({data.totals.approvedHours} h approved)</>}
          {money && data.totals.valueCents !== null && <> · {rand(data.totals.valueCents)} of chargeable time approved</>}
        </span>
      </h2>
      {data.entries.length === 0 ? (
        <p className="m-0 text-[13.5px] text-eq-neutral">No time on this client in these dates.</p>
      ) : (
        <table className="w-full border-collapse text-[13.5px]">
          <thead className="text-[12px] text-eq-neutral">
            <tr>
              <th className={thCls}>Date</th>
              {money && <th className={thCls}>Who</th>}
              <th className={thCls}>Billing job</th>
              <th className={thCls}>Narrative</th>
              <th className={numTh}>Hours</th>
              <th className={thCls}>Status</th>
              {money && <th className={numTh}>Value</th>}
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id} className="border-t border-eq-border">
                <td className={`${tdCls} whitespace-nowrap`}>{dayLabel(e.workDate)}</td>
                {money && <td className={tdCls}>{e.person.name}</td>}
                <td className={tdCls}>
                  {e.job.name}
                  {e.task !== null && <span className="text-eq-helper"> · {e.task}</span>}
                  {!e.job.chargeable && <div className="text-[12px] text-eq-helper">not chargeable</div>}
                </td>
                <td className={tdCls}>{e.narrative}</td>
                <td className={numCls}>{e.hours}</td>
                <td className={tdCls}>
                  <StatusChip status={e.status} />
                </td>
                {money && <td className={numCls}>{e.valueCents === null ? '' : rand(e.valueCents)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function Disbursements({ data }: { data: ClientTime }) {
  return (
    <section className={sectionCls}>
      <h2 className="mb-2 mt-0 font-heading text-[15px] font-bold text-eq-navy">Disbursements</h2>
      <table className="w-full border-collapse text-[13.5px]">
        <thead className="text-[12px] text-eq-neutral">
          <tr>
            <th className={thCls}>Date</th>
            {data.scope === 'all' && <th className={thCls}>Who</th>}
            <th className={thCls}>What</th>
            <th className={numTh}>Amount</th>
            <th className={thCls}>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.disbursements.map((x) => (
            <tr key={x.id} className="border-t border-eq-border">
              <td className={`${tdCls} whitespace-nowrap`}>{dayLabel(x.incurredDate)}</td>
              {data.scope === 'all' && <td className={tdCls}>{x.person.name}</td>}
              <td className={tdCls}>{x.description}</td>
              <td className={numCls}>{rand(x.amountCents)}</td>
              <td className={tdCls}>
                <StatusChip status={x.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
